// Location detection for Usercentrics CMP selection
// This determines which CMP framework to load based on user location

import {
  CCPA_SETTINGS_ID,
  CCPA_STATES,
  EEA_COUNTRIES,
  EU_COUNTRIES,
  // GDPR_COUNTRIES,
  GDPR_SETTINGS_ID,
  languageMap,
  TCF_SETTINGS_ID,
  timezoneMap,
} from "../constants";

export enum Regulation {
  TCF = "TCF",
  GDPR = "GDPR",
  CCPA = "CCPA",
  NONE = "NONE",
}

export interface LocationInfo {
  country: string;
  region?: string;
  regulation: Regulation;
  isEU: boolean;
  isEEA: boolean;
  isCalifornia: boolean;
}

/**
 * Determine regulation based on country and region
 */
export function determineRegulation(country: string, region?: string): LocationInfo {
  const isEU = EU_COUNTRIES.has(country);
  const isEEA = EEA_COUNTRIES.has(country);
  // const isGDPR = GDPR_COUNTRIES.has(country);
  const isCalifornia = country === "US" && region === "CA";

  let regulation: Regulation = Regulation.GDPR; // GDPR for every country as required

  if (isEEA) {
    regulation = Regulation.TCF; // TCF 2.2 for EU/EEA
  } else if (country === "US") {
    regulation = Regulation.CCPA;
  }

  return {
    country,
    region,
    regulation,
    isEU,
    isEEA,
    isCalifornia: isCalifornia || (country === "US" && region ? CCPA_STATES.has(region) : false),
  };
}

/**
 * Get CMP settings ID based on regulation
 */
export function getCMPSettingsId(regulation: Regulation): string | null {
  switch (regulation) {
    case Regulation.TCF:
      return TCF_SETTINGS_ID;
    case Regulation.GDPR:
      return GDPR_SETTINGS_ID;
    case Regulation.CCPA:
      return CCPA_SETTINGS_ID;
    case Regulation.NONE:
      return null;
    default:
      return null;
  }
}

/**
 * Check if consent is required for this location
 */
export function requiresConsent(locationInfo: LocationInfo): boolean {
  return locationInfo.regulation !== Regulation.NONE;
}

/**
 * Detect user location using browser-based methods
 */
export async function detectLocation(): Promise<LocationInfo> {
  // IP detection
  const ipLocation = await detectFromIP();
  if (ipLocation) {
    return ipLocation;
  }

  // Fallback to timezone detection
  const timezoneLocation = detectFromTimezone();
  if (timezoneLocation) {
    return timezoneLocation;
  }

  // Final fallback to language detection
  const languageLocation = detectFromLanguage();
  if (languageLocation) {
    return languageLocation;
  }

  // Default fallback
  console.warn("Location detection failed, defaulting to US");
  return determineRegulation("US");
}

/**
 * Detect location from IP
 */
async function detectFromIP(): Promise<LocationInfo | null> {
  try {
    const response = await fetch("https://ipapi.co/json/");
    const data = await response.json();

    if (data.country_code) {
      return determineRegulation(data.country_code.toUpperCase(), data.region_code?.toUpperCase());
    }

    return null;
  } catch (error) {
    console.warn("IP detection failed:", error);
    return null;
  }
}

/**
 * Detect location from timezone
 */
function detectFromTimezone(): LocationInfo | null {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const country = getCountryFromTimezone(timezone);
    if (country) {
      return determineRegulation(country);
    }
    return null;
  } catch (error) {
    console.warn("Timezone detection failed:", error);
    return null;
  }
}

/**
 * Detect location from language
 */
function detectFromLanguage(): LocationInfo | null {
  try {
    const language = navigator.language.split("-")[0];
    const country = getCountryFromLanguage(language);
    if (country) {
      return determineRegulation(country);
    }
    return null;
  } catch (error) {
    console.warn("Language detection failed:", error);
    return null;
  }
}

/**
 * Map language codes to countries
 */
function getCountryFromLanguage(language: string): string | null {
  return languageMap[language] || null;
}

/**
 * Map timezones to countries
 */
function getCountryFromTimezone(timezone: string): string | null {
  return timezoneMap[timezone] || null;
}
